import { Link } from "react-router-dom";
import { MapPin, Clock, Gavel, Building2, Calendar } from "lucide-react";
import { money, catLabel, PERIODS, FALLBACK_IMG } from "@/lib/format";
import { Countdown } from "@/components/Countdown";
import { RefCode } from "@/components/RefCode";

const PriceLine = ({ l }) => {
  if (l.type === "auction") {
    return (
      <div className="flex items-end justify-between pt-3 border-t border-slate-100">
        <div>
          <div className="text-[11px] font-medium text-slate-500">{l.current_bid ? "أعلى مزايدة" : "سعر الافتتاح"}</div>
          <div className="text-lg font-bold text-slate-900 tabular-nums">{money(l.current_bid || l.start_price)}</div>
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-1"><Gavel size={14} />{l.bids_count || 0} مزايدة</div>
      </div>
    );
  }
  if (l.type === "job") return <div className="text-sm font-bold text-slate-900">{l.salary || "الراتب حسب الخبرة"}</div>;
  if (l.type === "invest") return (
    <div className="pt-3 border-t border-slate-100 flex items-end justify-between">
      <div><div className="text-[11px] font-medium text-slate-500">المبلغ المطلوب</div><div className="text-lg font-bold text-slate-900 tabular-nums">{money(l.price)}</div></div>
      {l.stake_percent && <div className="text-xs font-semibold text-slate-600 tabular-nums">حصة {l.stake_percent}%</div>}
    </div>
  );
  return (
    <div className="pt-3 border-t border-slate-100 text-lg font-bold text-slate-900 tabular-nums">
      {money(l.price)} {l.type === "rent" && <span className="text-xs font-semibold text-slate-500">{PERIODS[l.rent_period]}</span>}
    </div>
  );
};

const JobCard = ({ l }) => (
  <Link to={`/listing/${l.id}`} data-testid={`listing-card-${l.id}`} className="block bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all">
    <div className="min-w-0">
      <h3 className="text-base font-bold text-slate-900 truncate">{l.title}</h3>
      <div className="mt-1"><RefCode code={l.ref_code} testId={`card-ref-${l.id}`} /></div>
      <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-1"><Building2 size={14} />{l.company}</div>
    </div>
    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-xs text-slate-500">
      <span>{catLabel("job", l.category)}</span>
      {l.job_type && <span>{l.job_type}</span>}
      {l.city && <span className="flex items-center gap-1"><MapPin size={12} />{l.city}</span>}
    </div>
    <div className="mt-4 pt-3 border-t border-slate-100"><PriceLine l={l} /></div>
  </Link>
);

export const ListingCard = ({ l }) => {
  if (l.type === "job") return <JobCard l={l} />;
  return (
    <Link to={`/listing/${l.id}`} data-testid={`listing-card-${l.id}`} className="group flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md hover:border-slate-300 transition-all">
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
        <img src={l.image || FALLBACK_IMG[l.type]} alt={l.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        <span className="absolute top-3 right-3 bg-white/95 text-slate-800 text-[11px] font-semibold px-2 py-1 rounded-md shadow-sm">{catLabel(l.type, l.category)}</span>
        {l.type === "auction" && (
          <div className={`absolute bottom-3 left-3 px-2.5 py-1 rounded-md text-xs font-mono font-bold flex items-center gap-1.5 border ${l.is_ended ? "bg-red-600 text-white border-red-500" : "bg-slate-900/90 text-white border-white/10 backdrop-blur-md"}`}>
            {!l.is_ended && <Clock size={12} />}
            <Countdown end={l.end_time} testId={`card-countdown-${l.id}`} />
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900 line-clamp-1">{l.title}</h3>
          <div className="mt-1.5"><RefCode code={l.ref_code} testId={`card-ref-${l.id}`} /></div>
          <div className="text-xs text-slate-500 flex items-center gap-3 mt-1.5">
            {l.city && <span className="flex items-center gap-1"><MapPin size={12} />{l.city}</span>}
            {l.year && <span className="flex items-center gap-1"><Calendar size={12} />{l.year}</span>}
          </div>
        </div>
        <PriceLine l={l} />
      </div>
    </Link>
  );
};
